import React, { useState, useMemo, useCallback, useEffect } from "react";
import { View, Text, Pressable, StyleSheet, Platform, TextStyle } from "react-native";
import { ShieldWarningIcon, DotsThreeVerticalIcon, UserIcon } from "@phosphor-icons/react";
import { colors, radii, spacing, typographyScale, shadowStyles } from "../../../../styles/design-system";
import { SeverityBadge, getSeverityConfig } from "./TriageBadge";
import { SafetyCase, Severity } from "@axio/graphql/generated/graphql";
import { formatTimeAgo } from "../../../../utils/date";

const getTimeSeverityStyle = (date: string, severity: Severity): TextStyle => {
  const dateDate = new Date(date);
  const hours = (new Date().getTime() - dateDate.getTime()) / (1000 * 60 * 60);
  if (severity === Severity.Critical && hours > 2) {
    return { color: colors.error[700], fontWeight: "600" };
  }
  if (severity === Severity.High && hours > 4) {
    return { color: colors.warning[800] };
  }
  if (hours > 24) {
    return { color: colors.warning[700] };
  }
  return { color: colors.grey[600] };
};

interface ActionMenuItem {
  label: string;
  value: string;
}

interface ActionsMenuProps {
  options: ActionMenuItem[];
  onSelect: (value: string) => void;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  accessibilityLabel?: string;
}

// ACTIONS MENU
function ActionsMenu({ options, onSelect, isOpen, onOpenChange, accessibilityLabel }: ActionsMenuProps) {
  const isWeb = Platform.OS === "web";
  const [isTriggerHovered, setIsTriggerHovered] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  // Close menu when clicking outside (web only)
  useEffect(() => {
    if (!isWeb || !isOpen) return;

    const handleClickOutside = () => {
      onOpenChange(false);
    };

    // Small delay to avoid immediate close on the same click
    const timer = setTimeout(() => {
      document.addEventListener("click", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isWeb, isOpen, onOpenChange]);

  const handleSelect = useCallback(
    (value: string) => {
      onOpenChange(false);
      onSelect(value);
    },
    [onSelect, onOpenChange]
  );

  return (
    <View style={[actionsMenuStyles.container, isOpen && actionsMenuStyles.containerOpen]}>
      <Pressable
        onPress={(e) => {
          e.stopPropagation?.();
          onOpenChange(!isOpen);
        }}
        onHoverIn={() => setIsTriggerHovered(true)}
        onHoverOut={() => setIsTriggerHovered(false)}
        style={({ pressed }) => [
          actionsMenuStyles.trigger,
          isTriggerHovered && actionsMenuStyles.triggerHovered,
          pressed && actionsMenuStyles.triggerPressed,
        ]}
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="button">
        <DotsThreeVerticalIcon size={20} weight="bold" color={isTriggerHovered ? colors.grey[950] : colors.grey[600]} />
      </Pressable>

      {isOpen && (
        <View style={actionsMenuStyles.menu}>
          {options.map((option) => (
            <Pressable
              key={option.value}
              onPress={(e) => {
                e.stopPropagation?.();
                handleSelect(option.value);
              }}
              onHoverIn={() => setHoveredItem(option.value)}
              onHoverOut={() => setHoveredItem(null)}
              style={({ pressed }) => [
                actionsMenuStyles.menuItem,
                hoveredItem === option.value && actionsMenuStyles.menuItemHovered,
                pressed && actionsMenuStyles.menuItemPressed,
              ]}>
              <Text style={actionsMenuStyles.menuItemText}>{option.label}</Text>
            </Pressable>
          ))}
        </View>
      )}
    </View>
  );
}

const actionsMenuStyles = StyleSheet.create({
  container: {
    position: "relative",
    zIndex: 1,
  },
  containerOpen: {
    zIndex: 10000,
  },
  trigger: {
    width: spacing.xl,
    height: spacing.xl,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: radii.sm,
  },
  triggerHovered: {
    backgroundColor: colors.grey[100],
  },
  triggerPressed: {
    backgroundColor: colors.grey[200],
  },
  menu: {
    position: "absolute",
    top: 36,
    right: 0,
    backgroundColor: colors.white,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.grey[200],
    minWidth: 220,
    paddingVertical: spacing.xxs,
    zIndex: 10000,
    ...shadowStyles.lg,
  },
  menuItem: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: radii.sm,
    marginHorizontal: spacing.xxs,
  },
  menuItemHovered: {
    backgroundColor: colors.grey[50],
  },
  menuItemPressed: {
    backgroundColor: colors.grey[100],
  },
  menuItemText: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    color: colors.grey[700],
  },
});

interface TriageTableRowProps {
  item: SafetyCase;
  onOpenDetail: (item: SafetyCase) => void;
  isMenuOpen?: boolean;
  onMenuOpenChange?: (isOpen: boolean) => void;
}

export function TriageTableRow({ item, onOpenDetail, isMenuOpen = false, onMenuOpenChange }: TriageTableRowProps) {
  const severityConfig = getSeverityConfig(item.severity);
  const timeSeverityStyle = getTimeSeverityStyle(item.time, item.severity);

  // MENU OPTIONS
  const menuOptions: ActionMenuItem[] = useMemo(() => {
    return [{ label: "View Details", value: "view-details" }];
  }, []);

  const handleMenuSelect = useCallback(
    (value: string) => {
      switch (value) {
        case "view-details":
          onOpenDetail(item);
          break;
      }
    },
    [item, onOpenDetail]
  );

  const handleMenuOpenChange = useCallback(
    (isOpen: boolean) => {
      onMenuOpenChange?.(isOpen);
    },
    [onMenuOpenChange]
  );

  const [isHovered, setIsHovered] = useState(false);

  return (
    <Pressable
      onPress={() => onOpenDetail(item)}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={({ pressed }) => [
        styles.row,
        { backgroundColor: severityConfig.rowBgColor },
        isHovered && styles.rowHovered,
        pressed && styles.rowPressed,
        isMenuOpen && styles.rowMenuOpen,
      ]}>
      {/* Severity */}
      <View style={styles.severityCell}>
        <SeverityBadge severity={item.severity} />
      </View>

      {/* Type */}
      <View style={styles.typeCell}>
        <View style={styles.typeContent}>
          {/* {item.type === "safety-alert" ? (
            <ShieldWarning size={16} color={colors.error[500]} />
          ) : (
            <Question size={16} color={colors.blue[500]} />
          )} */}
          <ShieldWarningIcon size={16} color={colors.error[500]} />
          {/* <Text style={styles.typeText}>{item.type === "safety-alert" ? "Safety" : "Support"}</Text> */}
          <Text style={styles.typeText}>Safety</Text>
        </View>
      </View>

      {/* Student */}
      <View style={styles.studentCell}>
        <View style={styles.studentContent}>
          <View style={styles.avatar}>
            <UserIcon size={16} color={colors.grey[400]} />
          </View>
          <Text style={styles.studentName} numberOfLines={1}>
            {item.studentName}
          </Text>
        </View>
      </View>

      {/* Category */}
      <View style={styles.categoryCell}>
        <Text style={styles.categoryText} numberOfLines={1}>
          {item.categories?.join(", ")}
        </Text>
      </View>

      {/* Time */}
      <View style={styles.timeCell}>
        <Text style={[styles.timeText, timeSeverityStyle]}>{formatTimeAgo(item.time)}</Text>
      </View>

      {/* Status */}
      {/* <View style={styles.statusCell}>
        <StatusBadge status={item.status} />
      </View> */}

      {/* Actions */}
      <View style={[styles.actionsCell, isMenuOpen && styles.actionsCellOpen]}>
        <ActionsMenu
          options={menuOptions}
          onSelect={handleMenuSelect}
          isOpen={isMenuOpen}
          onOpenChange={handleMenuOpenChange}
          accessibilityLabel={`Actions for ${item.studentName}`}
        />
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    minHeight: 65,
    position: "relative",
    zIndex: 1,
    overflow: "visible",
  },
  rowHovered: {
    backgroundColor: colors.grey[50],
  },
  rowPressed: {
    backgroundColor: colors.grey[100],
  },
  rowMenuOpen: {
    zIndex: 10000,
    overflow: "visible",
  },
  severityCell: {
    flex: 1,
    minWidth: 80,
    paddingHorizontal: spacing.sm,
  },
  typeCell: {
    flex: 1,
    minWidth: 70,
    paddingHorizontal: spacing.sm,
  },
  typeContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  typeText: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    color: colors.grey[600],
  },
  studentCell: {
    flex: 1.5,
    minWidth: 120,
    paddingHorizontal: spacing.sm,
  },
  studentContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  avatar: {
    width: spacing.xl,
    height: spacing.xl,
    borderRadius: radii.lg,
    backgroundColor: colors.grey[100],
    alignItems: "center",
    justifyContent: "center",
  },
  studentName: {
    flex: 1,
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "400",
    color: colors.grey[900],
  },
  categoryCell: {
    flex: 1.2,
    minWidth: 100,
    paddingHorizontal: spacing.sm,
  },
  categoryText: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    color: colors.grey[600],
  },
  timeCell: {
    flex: 1,
    minWidth: 70,
    paddingHorizontal: spacing.sm,
  },
  timeText: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  actionsCell: {
    width: spacing["3xl"],
    paddingHorizontal: spacing.sm,
    alignItems: "center",
    position: "relative",
    zIndex: 1,
    overflow: "visible",
  },
  actionsCellOpen: {
    zIndex: 10000,
    overflow: "visible",
  },
});
